import React, { useCallback, useEffect, useState } from "react";
import { Check, Edit3, History, Percent, Plus, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionIcon } from "./ActionIcon";
import RateHistory from "./RateHistory";
import FeeCalculation from "./FeeCalculation";
import RateChangeLog from "./RateChangeLog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const TABS = [["rates", "Rates"], ["history", "Rate History"], ["fees", "Fee Calculation"]];

const BLANK = { company: "", rate_type: "", start_date: "", end_date: "", rate_percent: "", notes: "" };

const asPercentInput = (value) => (value === "" ? "" : String(Number(value) * 100));
const toFraction = (value) => Number(value || 0) / 100;

/** Fee rates behind carrier fees, Employees First fees and Purchasing Power fees. */
export default function RatesManager() {
  const [report, setReport] = useState({ rate_types: [], rows: [] });
  const [employers, setEmployers] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("rates");
  const [logRow, setLogRow] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/rates");
    setReport(data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/admin/companies")
      .then(({ data }) => setEmployers(data.filter((c) => c.registered).map((c) => c.name)))
      .catch(() => setEmployers([]));
  }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.company || !form.rate_type) return toast.error("Choose an employer and a rate type");
    setSaving(true);
    const payload = { ...form, rate_percent: toFraction(form.rate_percent) };
    try {
      const saved = editingId
        ? await api.put(`/admin/rates/${editingId}`, payload)
        : await api.post("/admin/rates", payload);
      toast.success(editingId ? "Rate updated" : "Rate added");
      (saved.data.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      setForm(BLANK);
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const edit = (row) => {
    setEditingId(row.id);
    setForm({
      company: row.company, rate_type: row.rate_type, start_date: row.start_date,
      end_date: row.end_date, rate_percent: asPercentInput(row.rate_percent), notes: row.notes || "",
    });
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the ${row.rate_type} rate for ${row.company} starting ${row.start_date}?`)) return;
    try {
      await api.delete(`/admin/rates/${row.id}`);
      toast.success("Rate deleted");
      if (editingId === row.id) { setEditingId(null); setForm(BLANK); }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const release = async (row) => {
    if (!window.confirm(`Hand the ${row.rate_type} rate for ${row.company} back to the monthly workbook? The next upload will overwrite it.`)) return;
    try {
      await api.post(`/admin/rates/${row.id}/release`);
      toast.success("Rate released to the feed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const picker = (label, value, options, onChange, testId) => (
    <div>
      <label className="field-label">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid={testId}>
          <SelectValue placeholder={`Select ${label.toLowerCase()}`}/>
        </SelectTrigger>
        <SelectContent className="!rounded-none max-h-72">
          {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div data-testid="rates-manager">
      <div className="flex gap-6 border-b mb-6" style={{ borderColor: "var(--border)" }} data-testid="rates-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className="eyebrow pb-3 -mb-px transition-colors"
            style={{
              color: tab === key ? "var(--ink)" : "var(--muted)",
              borderBottom: tab === key ? "2px solid var(--accent, var(--ink))" : "2px solid transparent",
            }}
            data-testid={`rates-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "history" && <RateHistory employers={employers} />}
      {tab === "fees" && <FeeCalculation employers={employers} />}
      {tab !== "rates" ? null : (<>
      <p className="text-sm max-w-2xl mb-5" style={{ color: "var(--muted)" }}>
        Effective-dated fee rates per employer. These drive carrier fees, Employees First fees and
        Purchasing Power fees. The monthly workbook refreshes them, except rows you add or edit here —
        those are marked <strong>Manual</strong> and survive every upload until you release them.
      </p>

      <form onSubmit={submit} className="border p-6 mb-6 space-y-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="rate-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {picker("Employer", form.company, employers, (v) => setForm({ ...form, company: v }), "rate-company")}
          {picker("Rate type", form.rate_type, report.rate_types, (v) => setForm({ ...form, rate_type: v }), "rate-type")}
          <div>
            <label className="field-label">Start date</label>
            <input className="input-flat" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rate-start"/>
          </div>
          <div>
            <label className="field-label">End date</label>
            <input className="input-flat" type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="rate-end"/>
          </div>
          <div>
            <label className="field-label">Rate (%)</label>
            <input className="input-flat" type="number" step="0.001" min="0" max="100" required value={form.rate_percent} onChange={(e) => setForm({ ...form, rate_percent: e.target.value })} data-testid="rate-percent"/>
          </div>
        </div>
        <div>
          <label className="field-label">Notes</label>
          <input className="input-flat" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Based on contract with Empyrean" data-testid="rate-notes"/>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="rate-submit">
            {editingId ? <Check size={14}/> : <Plus size={14}/>}
            {saving ? "Saving…" : editingId ? "Save Rate" : "Add Rate"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(BLANK); }} className="btn-ghost flex items-center gap-2" data-testid="rate-cancel">
              <X size={14}/> Cancel
            </button>
          )}
        </div>
      </form>

      <div className="border max-h-[460px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="rates-table-region">
        <table className="w-full text-xs min-w-[820px]" data-testid="rates-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-3 py-2">Employer</th>
              <th className="px-3 py-2">Rate type</th>
              <th className="px-3 py-2">Effective</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr key={row.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`rate-row-${row.id}`}>
                <td className="px-3 py-2 font-medium" style={{ color: "var(--ink)" }}>
                  <span className="flex items-center gap-2">
                    {row.company}
                    {row.source === "admin" && (
                      <span className="pill text-[9px]" title={row.edited_by ? `Last edited by ${row.edited_by} — protected from the monthly workbook` : "Protected from the monthly workbook"} data-testid={`rate-manual-${row.id}`}>
                        Manual
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="pill text-[10px] flex items-center gap-1 w-fit"><Percent size={10}/> {row.rate_type}</span>
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--muted)" }}>{row.start_date} → {row.end_date}</td>
                <td className="px-3 py-2 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>
                  {(row.rate_percent * 100).toFixed(2)}%
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--muted)" }}>{row.notes || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <ActionIcon icon={History} label="View the change history for this rate" onClick={() => setLogRow(row)} testId={`rate-log-${row.id}`}/>
                    <ActionIcon icon={Edit3} label="Edit this rate" onClick={() => edit(row)} testId={`edit-rate-${row.id}`}/>
                    {row.source === "admin" && (
                      <ActionIcon icon={Undo2} label="Release to the monthly workbook" onClick={() => release(row)} testId={`release-rate-${row.id}`}/>
                    )}
                    <ActionIcon icon={Trash2} label="Delete this rate" onClick={() => remove(row)} testId={`delete-rate-${row.id}`}/>
                  </span>
                </td>
              </tr>
            ))}
            {!report.rows.length && (
              <tr><td colSpan={6} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="rates-empty">
                No rates yet — add one above or load the monthly workbook.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>)}

      <Dialog open={!!logRow} onOpenChange={(open) => !open && setLogRow(null)}>
        <DialogContent className="!rounded-none max-w-xl max-h-[80vh] overflow-y-auto" data-testid="rate-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {logRow ? `${logRow.rate_type} · ${logRow.company}` : "Rate history"}
            </DialogTitle>
          </DialogHeader>
          {logRow && (
            <RateChangeLog company={logRow.company} rateType={logRow.rate_type}
              startDate={logRow.start_date}
              emptyNote="No changes recorded for this rate window yet."/>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
